// Freshwater season / spawn phase derived from water temperature.
//
// Bass (lake/reservoir) and trout (river/stream) anglers think in terms of a
// seasonal/spawn progression driven mostly by water temp. This turns a water
// temperature (°F) plus the angler's primary freshwater subtype into a short,
// plain-English phase label + coaching note. Location-independent; no network.
//
// subType: "river" | "lake" | "" (primary freshwater subtype)
// month:   0–11 (defaults to current month) — used only for a fall-turnover hint.
//
// Returns { label, note, status } or null when temp is unknown.
//   status: "good" | "warn" | "neutral"  (matches CondRow coloring)

const LAKE_PHASES = [
  { max: 40, label: "Winter", note: "Cold water — fish deep and slow.", status: "warn" },
  { max: 48, label: "Cold / Pre-Spawn", note: "Bass staging deep near spawning flats.", status: "neutral" },
  { max: 55, label: "Pre-Spawn", note: "Prime feeding window as bass move shallow.", status: "good" },
  { max: 65, label: "Spawn", note: "Bass on beds — sight-fishing and reaction baits.", status: "good" },
  { max: 72, label: "Post-Spawn", note: "Recovering fish; target shade and cover.", status: "good" },
  { max: 82, label: "Summer", note: "Fish early/late; look for deep structure midday.", status: "neutral" },
  { max: 999, label: "Hot / Low O₂", note: "Heat slows the bite — dawn, dusk, night.", status: "warn" },
];

const RIVER_PHASES = [
  { max: 40, label: "Cold / Slow", note: "Trout lethargic — fish deep, slow, midday.", status: "warn" },
  { max: 50, label: "Cool", note: "Bite builds midday; nymphs and streamers.", status: "neutral" },
  { max: 65, label: "Prime", note: "Ideal trout temps — expect active hatches.", status: "good" },
  { max: 68, label: "Warm — Fish Early", note: "Fish morning; oxygen drops as it warms.", status: "neutral" },
  { max: 75, label: "Stress (>68°F)", note: "Trout stressed — fish dawn or move to cooler water.", status: "warn" },
  { max: 999, label: "Too Warm", note: "Rest the fishery — water is dangerously warm for trout.", status: "warn" },
];

export function getSeasonPhase(waterTempF, subType = "", month = new Date().getMonth()) {
  if (waterTempF == null || Number.isNaN(Number(waterTempF))) return null;
  const t = Number(waterTempF);
  const isRiver = String(subType || "").toLowerCase() === "river";
  const table = isRiver ? RIVER_PHASES : LAKE_PHASES;
  const base = table.find((row) => t < row.max) || table[table.length - 1];

  // Fall-turnover feeding hint for lakes: cooling water in autumn (Sep–Nov)
  // through the 50–65°F band triggers a heavy feed as lakes turn over.
  if (!isRiver && (month >= 8 && month <= 10) && t >= 50 && t <= 65) {
    return {
      label: "Fall Feed / Turnover",
      note: "Cooling water triggers a heavy pre-winter feed.",
      status: "good",
    };
  }

  return { label: base.label, note: base.note, status: base.status };
}

export default getSeasonPhase;
